import React, { useState, useMemo, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DropdownBox from "../../Shared/DropdownBox";
import {
  faPlus,
  faBookOpen,
  faIdCard,
  faPlay,
  faRedo,
  faPaperPlane,
  faTimes,
  faFileAlt,
  faEdit,
  faPrint,
  faTrash,
  faStar,
  faUsers,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../Shared/Loader";
import { formatDate } from "../../../Helpers/dateHelpers";
import ApiServices from "../../Services/ApiServices";
import { toastHelper } from "../../../Helpers/toastHelper";
import { getStatusColor, getStatusText } from "../../../Helpers/statusHelpers";
import WarningAlert from "../../Shared/WarningAlert";
import DynamicAlert from "../../Shared/DynamicAlert";
import DeleteAlert from "../../Shared/DeleteAlert";
import { preparePrintExam } from "../../../Helpers/printHelper";
import { useNavigate } from "react-router-dom";
import SoftGrid from "../../Shared/SoftGrid/SoftGrid";
import extractErrorMessage from "../../../Helpers/APIHelper";
import { storageHelper } from "../../../Helpers/storageHelper";
import ModalPopUp from "../../Shared/ModalPopUp";
import DynamicModal from "../../Shared/DynamicModal";
import { useLanguage } from '../../../contexts/LanguageContext';

const IN_PROGRESS_STATUS = 2; // Replace with API value if needed

const EMPTY_EXAM_FORM = {
  exam_title: "",
  exam_description: "",
  exam_date: "",
  exam_time: "",
  exam_duration: "",
  user_class_id: "",
  user_class_name: "",
  question_weight: "1",
  is_random_questions: false,
  use_scratchpad: false,
};

const ExamManager = () => {
  const { t } = useLanguage();
  const [showAddExam, setShowAddExam] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedExam, setSelectedExam] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedExamForDetails, setSelectedExamForDetails] = useState(null);
  const [startExamLoading, setStartExamLoading] = useState(null);
  const [examToReset, setExamToReset] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [stopExamLoading, setStopExamLoading] = useState(null);
  const [sendToParentsLoading, setSendToParentsLoading] = useState(null);
  const [printLoading, setPrintLoading] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignExam, setAssignExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [inProgressExam, setInProgressExam] = useState(null);

  const [completedStudents, setCompletedStudents] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const navigate = useNavigate();
  const [restartLoadingUserId, setRestartLoadingUserId] = useState(null);
  const [examFormInitialData, setExamFormInitialData] = useState(EMPTY_EXAM_FORM);
  const [examModalLoading, setExamModalLoading] = useState(false);

  const [dynamicAlertState, setDynamicAlertState] = useState({
    isOpen: false,
    resolve: null,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    icon: "warning"
  });

  const showDynamicAlert = (options) => {
    return new Promise((resolve) => {
      setDynamicAlertState({
        isOpen: true,
        resolve,
        title: options.title,
        message: options.text,
        confirmText: options.confirmButtonText || t('confirm'),
        cancelText: options.cancelButtonText || t('cancel'),
        icon: options.icon || "warning"
      });
    });
  };

  const handleDynamicAlertConfirm = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: true });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDynamicAlertCancel = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: false });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const parseDurationToMinutes = (durationValue) => {
    if (!durationValue) return "";
    const parts = String(durationValue).split(":").map(Number);
    if (parts.length >= 2 && parts.every((p) => !Number.isNaN(p))) {
      return String(parts[0] * 60 + parts[1]);
    }
    return String(durationValue);
  };

  const mapExamToFormData = (examDetails) => {
    const resolvedClassId =
      examDetails?.user_class_id ??
      examDetails?.userClassId ??
      examDetails?.class_id ??
      examDetails?.user_class?.id ??
      "";

    const resolvedClassName =
      examDetails?.user_class_name ??
      examDetails?.userClassName ??
      examDetails?.user_class?.name ??
      "";

    let formattedDate = "";
    let examTime = "";
    if (examDetails?.exam_date && !Number.isNaN(new Date(examDetails.exam_date).getTime())) {
      const dateObj = new Date(examDetails.exam_date);
      formattedDate = dateObj.toISOString().split("T")[0];
      examTime = dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    return {
      exam_title: examDetails?.exam_title ? String(examDetails.exam_title) : "",
      exam_description: examDetails?.exam_description ? String(examDetails.exam_description) : "",
      exam_date: formattedDate,
      exam_time: examTime,
      exam_duration: parseDurationToMinutes(examDetails?.exam_duration),
      user_class_id: resolvedClassId !== "" && resolvedClassId !== null && resolvedClassId !== undefined
        ? String(resolvedClassId)
        : "",
      user_class_name: resolvedClassName ? String(resolvedClassName) : "",
      question_weight: examDetails?.question_weight ? String(examDetails.question_weight) : "1",
      is_random_questions: Boolean(examDetails?.is_random_questions),
      use_scratchpad: Boolean(examDetails?.use_scratchpad),
    };
  };

  const examFormConfig = useMemo(
    () => [
      {
        name: "exam_title",
        label: t('examTitle'),
        type: "text",
        required: true,
        min: 5,
        requiredMessage: t('examTitleRequired'),
        minMessage: t('examTitleMinLength'),
        defaultValue: examFormInitialData.exam_title,
      },
      {
        name: "user_class_id",
        label: t('examClass'),
        type: "async-select",
        required: true,
        requiredMessage: t('examClassRequired'),
        optionsUrl: "Lookup/UserClasses/Teacher",
        optionsMap: (item) => ({ value: String(item.id), label: item.name }),
        defaultValue: examFormInitialData.user_class_id,
        valueLabel: examFormInitialData.user_class_name,
      },
      {
        name: "exam_date",
        label: t('examDate'),
        type: "date",
        required: true,
        requiredMessage: t('examDateRequired'),
        defaultValue: examFormInitialData.exam_date,
      },
      {
        name: "exam_time",
        label: t('examTime'),
        type: "time",
        required: true,
        requiredMessage: t('examTimeRequired'),
        defaultValue: examFormInitialData.exam_time,
      },
      {
        name: "exam_duration",
        label: t('examDurationInMinutes'),
        type: "number",
        required: true,
        min: 1,
        requiredMessage: t('examDurationRequired'),
        minMessage: t('examDurationMin'),
        defaultValue: examFormInitialData.exam_duration,
      },
      {
        name: "question_weight",
        label: t('examQuestionWeight'),
        type: "number",
        required: true,
        min: 0.01,
        requiredMessage: t('questionWeightRequired'),
        minMessage: t('questionWeightMin'),
        defaultValue: examFormInitialData.question_weight,
      },
      {
        name: "is_random_questions",
        label: t('examQuestionOrdering'),
        type: "select",
        defaultValue: examFormInitialData.is_random_questions,
        options: [
          { value: false, label: t('normalOrder') },
          { value: true, label: t('randomOrder') },
        ],
      },
      {
        name: "use_scratchpad",
        label: t('useScratchpad'),
        type: "checkbox",
        defaultValue: examFormInitialData.use_scratchpad,
      },
      {
        name: "exam_description",
        label: t('examDescriptionOptional'),
        type: "text",
        multiline: true,
        rows: 4,
        defaultValue: examFormInitialData.exam_description,
      },
    ],
    [examFormInitialData]
  );

  useEffect(() => {
    fetchExams(currentPage, searchQuery);
  }, [currentPage, searchQuery, pageSize]);

  const fetchExams = async (page, searchWord = "") => {
    try {
      setLoading(true);
      const response = await ApiServices.get(
        `Exam/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true&Word=${encodeURIComponent(
          searchWord
        )}`
      );
      if (response.check) {
        setExams(response.data.items);
        setTotalPages(response.data.total_pages);
        setTotalRecords(response.data.total_records);
      }
    } catch (error) {
      toastHelper.error(t('examLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openCreateExamModal = () => {
    setEditingExam(null);
    setExamFormInitialData(EMPTY_EXAM_FORM);
    setShowAddExam(true);
  };

  const openEditExamModal = async (exam) => {
    try {
      setExamModalLoading(true);
      const response = await ApiServices.get(`Exam/${exam.id}`);
      const details = {
        ...(response?.data || {}),
        user_class_id:
          response?.data?.user_class_id ??
          exam?.user_class_id,
        user_class_name:
          response?.data?.user_class_name ??
          response?.data?.userClassName ??
          response?.data?.user_class?.name ??
          exam?.user_class_name,
      };
      setEditingExam(details);
      setExamFormInitialData(mapExamToFormData(details));
      setShowAddExam(true);
    } catch (error) {
      toastHelper.error(error?.msg || t('examLoadFailed'));
    } finally {
      setExamModalLoading(false);
    }
  };

  const handleExamModalSubmit = async (values) => {
    const payload = {
      exam_title: values.exam_title,
      exam_description: values.exam_description || null,
      exam_date: values.exam_date,
      exam_time: values.exam_time?.length === 5 ? `${values.exam_time}:00` : values.exam_time,
      exam_duration: parseInt(values.exam_duration, 10),
      user_class_id: parseInt(values.user_class_id, 10),
      question_weight: parseFloat(values.question_weight),
      is_random_questions:
        values.is_random_questions === true || values.is_random_questions === "true",
      use_scratchpad:
        values.use_scratchpad === true || values.use_scratchpad === "true",
    };

    try {
      let response;
      if (editingExam?.id) {
        response = await ApiServices.put(`Exam/update/${editingExam.id}`, payload);
        if (!response?.check) throw new Error(t('examUpdateError'));
        toastHelper.success(t('examUpdateSuccess'));
      } else {
        response = await ApiServices.post("Exam/create", payload);
        if (!response?.check) throw new Error(t('examAddError'));
        toastHelper.success(t('examAddSuccess'));
      }

      await fetchExams(currentPage, searchQuery);
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        toastHelper.error(extractErrorMessage(error));
      } else {
        toastHelper.error(error?.msg || error?.message || t('examSaveError'));
      }
      throw error;
    }
  };

  const formatDuration = (duration) => {
    // Convert TimeOnly format (HH:mm:ss) to hours and minutes for display
    const [hours, minutes] = duration.split(":");
    return t('examHoursFormat').replace('{hours}', hours).replace('{minutes}', minutes);
  };

  const filteredAndSortedExams = useMemo(() => {
    let filteredExams = [...exams];
    // Only apply status filter (search is server-side now)
    if (filterStatus !== "all") {
      filteredExams = filteredExams.filter(
        (exam) => exam.status === parseInt(filterStatus)
      );
    }
    return filteredExams;
  }, [exams, filterStatus]);

  const handleActionClick = (examId, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Get button position
    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    // Calculate if menu should open upward
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - buttonRect.bottom;
    const menuHeight = 200; // Approximate menu height

    // Set position class on the menu
  };

  const handleStartExam = async (exam) => {
    const result = await showDynamicAlert({
      title: t('examStartConfirmTitle'),
      text: t('examStartConfirmText').replace('{title}', exam.exam_title),
      icon: "question",
      confirmButtonText: t('examStartConfirmYes'),
      cancelButtonText: t('cancel'),
    });

    if (result.isConfirmed) {
      try {
        setStartExamLoading(exam.id);
        var res = await ApiServices.put(
          `Exam/start/${exam.id}/${exam.user_class_id}/${exam.material_id}`
        );
        toastHelper.success(t('examStartSuccess'));
        // Refresh the exams list
        await fetchExams(currentPage, searchQuery);
      } catch (error) {
        toastHelper.error(error.msg);
      } finally {
        setStartExamLoading(null);
      }
    }
  };
  const handleResetExam = async (exam) => {
    const result = await showDynamicAlert({
      title: t('examResetConfirmTitle'),
      text: t('examResetConfirmText'),
      icon: "warning",
      confirmButtonText: t('examResetConfirmYes'),
      cancelButtonText: t('cancel'),
    });

    if (result.isConfirmed) {
      try {
        setExamToReset(exam.id);
        await ApiServices.put(`Exam/reset/${exam.id}`);
        toastHelper.success(t('examResetSuccess'));
        // Refresh the exams list
        await fetchExams(currentPage, searchQuery);
      } catch (error) {
        toastHelper.error(error.msg || t('examResetError'));
      } finally {
        setExamToReset(null);
      }
    }
  };

  const handleStopExam = async (exam) => {
    const result = await showDynamicAlert({
      title: t('examStopConfirmTitle'),
      text: t('examStopConfirmText').replace('{title}', exam.exam_title),
      icon: "warning",
      confirmButtonText: t('examStopConfirmYes'),
      cancelButtonText: t('cancel'),
    });

    if (result.isConfirmed) {
      try {
        setStopExamLoading(exam.id);
        const res = await ApiServices.put(`Exam/Stop/${exam.id}`);
        if (res.check) {
          toastHelper.success(t('examStopSuccess'));
          // Refresh the exams list
          await fetchExams(currentPage, searchQuery);
        } else {
          toastHelper.error(t('examStopError'));
        }
      } catch (error) {
        toastHelper.error(error.msg || t('examStopError'));
      } finally {
        setStopExamLoading(null);
      }
    }
  };

  const handleSendToParents = async (exam) => {
    const result = await showDynamicAlert({
      title: t('examSendResultsConfirmTitle'),
      text: t('examSendResultsConfirmText').replace('{title}', exam.exam_title),
      icon: "question",
      confirmButtonText: t('examSendResultsConfirmYes'),
      cancelButtonText: t('cancel'),
    });

    if (result.isConfirmed) {
      try {
        setSendToParentsLoading(exam.id);
        const response = await ApiServices.post(
          `Exam/SendExamResultToParents/${exam.id}`
        );
        if (response.check) {
          toastHelper.success(t('examSendResultsSuccess'));
        } else {
          toastHelper.error(t('examSendResultsError'));
        }
      } catch (error) {
        toastHelper.error(error.msg || t('examSendResultsError'));
      } finally {
        setSendToParentsLoading(null);
      }
    }
  };

  const handleDelete = async (exam) => {
    setSelectedExam(exam);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedExam) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(
        `Exam/delete/${selectedExam.id}`
      );
      if (response.check) {
        toastHelper.success(t('examDeleteSuccess'));
        fetchExams(currentPage, searchQuery);
      }
    } catch (error) {
      toastHelper.error(t('examDeleteError'));
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedExam(null);
    }
  };

  // Fetch exam questions for a specific exam
  const fetchExamQuestions = async (examId) => {
    try {
      const response = await ApiServices.get(
        `ExamDetails/GetQuestions/${examId}`
      );
      var result = response.data || [];
      if (result.length == 0) toastHelper.error(t('examPrintNoQuestions'));
      else return result;
    } catch (error) {
      toastHelper.error(t('examPrintFetchError'));
      return [];
    }
  };

  // Handle printing an exam
  const handlePrintExam = async (exam) => {
    try {
      setPrintLoading(exam.id);
      await preparePrintExam(exam, fetchExamQuestions);
    } catch (error) {
    } finally {
      setPrintLoading(null);
    }
  };

  // Fetch students for teacher
  const fetchStudents = async () => {
    try {
      const response = await ApiServices.get("Lookup/Teachers/Students");
      setStudents(response);
    } catch (error) {
      toastHelper.error(t('studentLoadError'));
    }
  };

  // Handle assign action
  const handleAssignUser = (exam) => {
    setAssignExam(exam);
    setShowAssignModal(true);
    setSelectedStudent("");
    fetchStudents();
  };

  const handleAssignConfirm = async (values) => {
    const studentId = values.student_id;
    if (!assignExam || !studentId) return;

    const result = await showDynamicAlert({
      title: t('examAssignConfirmTitle'),
      text: t('examAssignConfirmText').replace('{title}', assignExam.exam_title),
      icon: "question",
      confirmButtonText: t('examAssignConfirmYes'),
      cancelButtonText: t('cancel'),
    });

    if (result.isConfirmed) {
      setAssignLoading(true);
      try {
        const response = await ApiServices.post(
          `UserExam/AssignUserExam/${assignExam.id}/${studentId}`,
          {}
        );
        toastHelper.success(t('examAssignSuccess'));
        setShowAssignModal(false);
      } catch (error) {
        const title = error?.title || error?.message || "";
        if (title.toLowerCase().includes("validation errors")) {
          const errMessage = extractErrorMessage(error);
          toastHelper.error(errMessage);
        } else {
          toastHelper.error(error.msg || t('examAssignError'));
        }
      } finally {
        setAssignLoading(false);
      }
    }
  };


  const AddExamForm = ({ onClose, onSubmit }) => {
  const { t } = useLanguage();
    const [formData, setFormData] = useState({
      exam_title: "",
      exam_description: "",
      exam_date: "",
      exam_time: "",
      exam_duration: "",
      material_id: "",
      user_class_id: "",
    });
    const [materials, setMaterials] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(true);

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLookupLoading(true);
          const [materialsData, classesData] = await Promise.all([
            ApiServices.get("Lookup/Materials"),
            ApiServices.get("Lookup/UserClasses"),
          ]);

          // Filter user classes based on localStorage user_class
          let allowedClassIds = [];
          const userClassStr = storageHelper.getItem("user_class");
          if (userClassStr) {
            allowedClassIds = userClassStr
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id));
          }

          // Only include classes whose id is in allowedClassIds
          const filteredClasses = allowedClassIds.length
            ? classesData.filter((cls) => allowedClassIds.includes(cls.id))
            : classesData;

          setMaterials(materialsData);
          setClasses(filteredClasses);
        } catch (error) {
          toastHelper.error(t('dataLoadError'));
        } finally {
          setLookupLoading(false);
        }
      };

      fetchData();
    }, []);

    useEffect(() => {
      if (editingExam) {
        const formattedDate = new Date(editingExam.exam_date)
          .toISOString()
          .split("T")[0];
        const examTime = new Date(editingExam.exam_date).toLocaleTimeString(
          "en-US",
          {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }
        );

        // Convert TimeOnly to minutes for the form
        const [hours, minutes] = editingExam.exam_duration
          .split(":")
          .map(Number);
        const durationInMinutes = hours * 60 + minutes;
        const editForm = {
          exam_title: editingExam.exam_title || "",
          exam_description: editingExam.exam_description || "",
          exam_date: formattedDate,
          exam_time: examTime,
          exam_duration: durationInMinutes,
          material_id: editingExam.material_id || "",
          user_class_id: editingExam.user_class_id || "",
        };
        setFormData(editForm);
        
      } else {
        setFormData({
          exam_title: "",
          exam_description: "",
          exam_date: "",
          exam_time: "",
          exam_duration: "",
          material_id: "",
          user_class_id: "",
        });
      }
    }, [editingExam]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        // Format the data according to API requirements
        const submitData = {
          exam_title: formData.exam_title,
          exam_description: formData.exam_description || null,
          exam_date: formData.exam_date,
          exam_time: new Date(formData.exam_date).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }),
          exam_duration: parseInt(formData.exam_duration), // Send as integer (minutes)
          material_id: parseInt(formData.material_id),
          user_class_id: parseInt(formData.user_class_id),
        };

        let response;
        if (editingExam) {
          response = await ApiServices.put(
            `Exam/update/${editingExam.id}`,
            submitData
          );
          if (response.check) {
            toastHelper.success(t('examUpdateSuccess'));
            await fetchExams(currentPage, searchQuery);
            onClose();
            setEditingExam(null);
          } else {
            toastHelper.error(t('examUpdateError'));
          }
        } else {
          response = await ApiServices.post("Exam/create", submitData);
          if (response.check) {
            toastHelper.success(t('examAddSuccess'));
            await fetchExams(currentPage, searchQuery);
            onClose();
          } else {
            toastHelper.error(t('examAddError'));
          }
        }
      } catch (error) {
        toastHelper.error(error.msg || t('examSaveError'));
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="add-exam-form">
        <div className="form-header">
          <h2>{editingExam ? t('editExamTitle') : t('addNewExamTitle')}</h2>
          <button
            className="close-btn"
            onClick={() => {
              onClose();
              setEditingExam(null);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {lookupLoading ? (
          <div className="form-loading">
            <Loader text={t('loadingData')} />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('examTitle')}</label>
              <input
                type="text"
                value={formData.exam_title}
                onChange={(e) =>
                  setFormData({ ...formData, exam_title: e.target.value })
                }
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('examMaterial')}</label>
                <select
                  value={formData.material_id}
                  onChange={(e) =>
                    setFormData({ ...formData, material_id: e.target.value })
                  }
                  required
                >
                  <option value="">{t('examMaterialSelect')}</option>
                  {materials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('examClass')}</label>
                <select
                  value={formData.user_class_id}
                  onChange={(e) =>
                    setFormData({ ...formData, user_class_id: e.target.value })
                  }
                  required
                >
                  <option value="">{t('examClassSelect')}</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('examDate')}</label>
                <input
                  type="date"
                  value={formData.exam_date}
                  onChange={(e) =>
                    setFormData({ ...formData, exam_date: e.target.value })
                  }
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="form-group">
                <label>{t('examTime')}</label>
                <input
                  type="time"
                  value={formData.exam_time}
                  onChange={(e) =>
                    setFormData({ ...formData, exam_time: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('durationInMinutes')}</label>
              <input
                type="number"
                value={formData.exam_duration}
                onChange={(e) =>
                  setFormData({ ...formData, exam_duration: e.target.value })
                }
                required
                min="1"
              />
            </div>
            <div className="form-group">
              <label>{t('examDescriptionOptional')}</label>
              <textarea
                value={formData.exam_description}
                onChange={(e) =>
                  setFormData({ ...formData, exam_description: e.target.value })
                }
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  onClose();
                  setEditingExam(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />{t('cancel')}</button>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? t('saving') : editingExam ? t('update') : t('save')}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  // Update handleSearch to call fetchExams with the search term and reset to page 1
  const handleSearch = (term) => {
    setSearchQuery(term);
    setCurrentPage(1);
    // fetchExams(1, term); // Not needed, useEffect will handle
  };

  useEffect(() => {
    if (showInProgressModal && inProgressExam) {
      setCompletedLoading(true);
      setCompletedStudents([]);
      ApiServices.get(`UserExam/getAllCompletedExam/${inProgressExam.id}`)
        .then((response) => {
          if (response.check && Array.isArray(response.data)) {
            setCompletedStudents(response.data);
          } else {
            setCompletedStudents([]);
          }
        })
        .catch(() => setCompletedStudents([]))
        .finally(() => setCompletedLoading(false));
    }
  }, [showInProgressModal, inProgressExam]);

  // زر إعادة الامتحان لكل طالب
  // احذف renderCompletedStudentActions واستخدم الزر داخل <ul> مباشرة

  if (
    loading ||
    startExamLoading ||
    stopExamLoading ||
    sendToParentsLoading ||
    printLoading
  ) {
    return (
      <Loader
        text={
          startExamLoading
            ? t('examStarting')
            : stopExamLoading
              ? t('examStopping')
              : sendToParentsLoading
                ? t('examSendingResults')
                : printLoading
                  ? t('examPrinting')
                  : t('examLoadingExams')
        }
      />
    );
  }
  if (loading || examToReset) {
    return <Loader text={t('examResetting')} />;
  }

  return (
    <div className="exam-manager">
      <div className="section-header">
        <div className="header-left">
          <h2>{t('examManagement')}</h2>
        </div>
        <div className="header-right">
          <div className="filter-group">
            <select
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">{t('examAllStatuses')}</option>
              <option value="1">{t('examUpcoming')}</option>
              <option value="2">{t('examInProgress')}</option>
              <option value="3">{t('completed')}</option>
            </select>
          </div>
          <button
            className="add-exam-btn"
            onClick={openCreateExamModal}
            disabled={examModalLoading}
          >
            <FontAwesomeIcon icon={faPlus} />
            {examModalLoading ? t('loading') : t('addNewExamTitle')}
          </button>
        </div>
      </div>

      <DynamicModal
        isOpen={showAddExam}
        onClose={() => {
          setShowAddExam(false);
          setEditingExam(null);
          setExamFormInitialData(EMPTY_EXAM_FORM);
        }}
        title={editingExam?.id ? t('editExamTitle') : t('addNewExamTitle')}
        submitButtonText={editingExam?.id ? t('updateExamBtn') : t('createExamBtn')}
        formConfig={examFormConfig}
        onSubmit={handleExamModalSubmit}
      />

      <WarningAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
      />

      <DynamicAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "question"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        confirmColor="#4a90e2"
      />

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedExam(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('examDeleteConfirmTitle')}
        message={t('examDeleteConfirmText')}
        itemName={selectedExam?.exam_title}
        itemDescription={`${t('examDatePrefix')}${formatDate(
          selectedExam?.exam_date
        )}`}
        loading={deleteLoading}
      />

      {/* Assign Student Modal */}
      <DynamicModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={`${t('examAssignStudentLabel')} ${assignExam?.exam_title || ''}`}
        submitButtonText={t('examAssignBtn')}
        formConfig={[
          {
            name: "student_id",
            label: t('examSelectStudent'),
            type: "select",
            required: true,
            searchable: true,
            options: students.map((s) => ({ value: s.id, label: s.name })),
          },
        ]}
        onSubmit={handleAssignConfirm}
      />

      <ModalPopUp
        isOpen={showInProgressModal && !!inProgressExam}
        showModalButtons={false}
        onClose={() => {
          setShowInProgressModal(false);
          setInProgressExam(null);
          setCompletedStudents([]);
        }}
        title={t('examInProgressModalTitle')}
        confirmText={t('examCloseBtn')}
        onConfirm={() => {
          setShowInProgressModal(false);
          setInProgressExam(null);
          setCompletedStudents([]);
        }}
      >
        {inProgressExam ? (
          <div style={{ padding: "1rem", textAlign: "right" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: "1.2rem",
                color: "#1a1f36",
                marginBottom: 8,
              }}
            >
              {inProgressExam.exam_title}
            </div>
            <div style={{ color: "#4a90e2", marginBottom: 4 }}>
              {t('examSubjectLabel')}{inProgressExam.material_name}
            </div>
            <div style={{ color: "#64748b", marginBottom: 4 }}>
              {t('examClassLabel')}{inProgressExam.user_class_name}
            </div>
            <div style={{ color: "#64748b", marginBottom: 4 }}>
              {t('examStatusLabel')}{inProgressExam.in_progress_status}
            </div>
            <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600 }}>
              {t('examPassedStudents')}
            </div>
            {completedLoading ? (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <span className="loader" />{t('loading')}</div>
            ) : completedStudents.length === 0 ? (
              <div style={{ color: "#888", textAlign: "center", padding: "1rem" }}>
                {t('examNoPassedStudents')}
              </div>
            ) : (
              <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
                {completedStudents.map((student) => (
                  <li
                    key={student.id}
                    style={{
                      background: "#f8fafc",
                      margin: "0.5rem 0",
                      padding: "0.7rem 1rem",
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(74,144,226,0.07)",
                      display: "flex",
                      flexDirection: "row-reverse",
                      alignItems: "center",
                      gap: "0.7rem",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#1a1f36", flex: 1 }}>
                      {student.user_name}
                    </span>
                    <span style={{ color: "#4a90e2" }}>
                      {t('examSubjectLabel')}{student.subject}
                    </span>
                    <span style={{ color: "#64748b" }}>
                      {t('examTestTitleLabel')}{student.title}
                    </span>
                    <button
                      title={t('examRetakeBtn')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3085d6',
                        cursor: restartLoadingUserId === student.user_id ? 'not-allowed' : 'pointer',
                        fontSize: 20,
                        marginRight: 8,
                        marginLeft: 8,
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      disabled={restartLoadingUserId === student.user_id}
                      onClick={async () => {
                        setShowInProgressModal(false);
                        const result = await showDynamicAlert({
                          title: t('examRetakeConfirmTitle'),
                          text: t('examRetakeConfirmText').replace('{name}', student.user_name),
                          icon: 'warning',
                          confirmButtonText: t('examRetakeConfirmYes'),
                          cancelButtonText: t('cancel'),
                        });
                        if (!result.isConfirmed) {
                          setShowInProgressModal(true);
                          return;
                        }
                        setRestartLoadingUserId(student.user_id);
                        try {
                          const res = await ApiServices.put(`UserExam/Restart/${inProgressExam.id}/${student.user_id}`);
                          if (res.check) {
                            toastHelper.success(t('examRetakeSuccess').replace('{name}', student.user_name));
                            // أعد جلب قائمة الطلاب المجتازين
                            setCompletedLoading(true);
                            setCompletedStudents([]);
                            const response = await ApiServices.get(`UserExam/getAllCompletedExam/${inProgressExam.id}`);
                            if (response.check && Array.isArray(response.data)) {
                              setCompletedStudents(response.data);
                            } else {
                              setCompletedStudents([]);
                            }
                            setCompletedLoading(false);
                          } else {
                            toastHelper.error(t('examRetakeError'));
                          }
                        } catch (error) {
                          toastHelper.error(error?.msg || t('examRetakeError'));
                        } finally {
                          setRestartLoadingUserId(null);
                        }
                      }}
                    >
                      {restartLoadingUserId === student.user_id ? (
                        <span className="circle-loader" />
                      ) : (
                        <FontAwesomeIcon icon={faRedo} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </ModalPopUp>



      <SoftGrid
        columns={[
          {
            key: "id",
            label: t('examIdLabel'),
            sortable: true,
            render: (value, row) => (
              <div className="exam-title-cell">
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: "exam_title",
            label: t('examTitle'),
            sortable: true,
          },
          {
            key: "questions_count",
            label: t('questionsCountLabel'),
            sortable: true,
          },
          {
            key: "user_class_name",
            label: t('examClass'),
            sortable: true,
          },
          {
            key: "exam_duration",
            label: t('examDuration'),
            sortable: true,
            render: (value) => formatDuration(value),
          },
          {
            key: "exam_date",
            label: t('examDate'),
            sortable: true,
            render: (value) => formatDate(value),
          },
          {
            key: "status",
            label: t('statusLabel'),
            render: (value, row) => (
              <div className="status-container">
                {value === IN_PROGRESS_STATUS ? (
                  <button
                    className="ex_status-badge ex_status-badge-btn"
                    style={{
                      backgroundColor: getStatusColor(value),
                      cursor: "pointer",
                      border: "none",
                      outline: "none",
                      borderRadius: "8",
                      height: "28px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInProgressExam(row);
                      setShowInProgressModal(true);
                    }}
                  >
                    {getStatusText(value)}
                  </button>
                ) : (
                  <div
                    className="ex_status-badge"
                    style={{
                      backgroundColor: getStatusColor(value),
                      border: "none",
                      outline: "none",
                      borderRadius: "15px",
                      height: "28px",
                    }}
                  >
                    {getStatusText(value)}
                  </div>
                )}
              </div>
            ),
          },
        ]}
        data={filteredAndSortedExams}
        actions={[
          (row) => {
            const exam = row;
            let actions = [];
            const addedTooltips = new Set();
            const addAction = (action) => {
              if (!addedTooltips.has(action.tooltip)) {
                actions.push(action);
                addedTooltips.add(action.tooltip);
              }
            };
            if (exam.status === 2 || exam.status === 3) {
              if (exam.status === 2) {
                addAction({
                  tooltip: t('stopExamTooltip'),
                  icon: <FontAwesomeIcon icon={faTimes} />,
                  onClick: () => handleStopExam(exam),
                  className: "warning",
                  disabled: stopExamLoading === exam.id,
                });
              }
              if (exam.status === 3) {
                addAction({
                  tooltip: t('sendToParentsTooltip'),
                  icon: <FontAwesomeIcon icon={faPaperPlane} />,
                  onClick: () => handleSendToParents(exam),
                  disabled: sendToParentsLoading === exam.id,
                });
              }
              addAction({
                tooltip: t('examResultsTooltip'),
                icon: <FontAwesomeIcon icon={faStar} />,
                onClick: async () => {
                  navigate(`/teacher/exam-result/${exam.id}`);
                },
              });
              addAction({
                tooltip: t('viewQuestionsTooltip'),
                icon: <FontAwesomeIcon icon={faFileAlt} />,
                onClick: async () => {
                  navigate(`/teacher/exams/details/${exam.id}`);
                },
              });
              addAction({
                tooltip: t('printExamTooltip'),
                icon: <FontAwesomeIcon icon={faPrint} />,
                onClick: () => handlePrintExam(exam),
                disabled: printLoading === exam.id,
              });
              if (exam.status === 3) {
                addAction({
                  tooltip: t('reset'),
                  icon: <FontAwesomeIcon icon={faRedo} />,
                  onClick: () => handleResetExam(exam),
                  disabled: examToReset === exam.id,
                });
              }
              if (exam.status !== 3) {
                addAction({
                  tooltip: t('assignStudentTooltip'),
                  icon: <FontAwesomeIcon icon={faPaperPlane} />,
                  onClick: async () => {
                    handleAssignUser(exam);
                  },
                  disabled:
                    assignLoading && assignExam && assignExam.id === exam.id,
                });
              }
            } else {
              [
                {
                  tooltip: t('edit'),
                  icon: <FontAwesomeIcon icon={faEdit} />,
                  onClick: () => openEditExamModal(exam),
                },
                {
                  tooltip: t('examQuestionsTooltip'),
                  icon: <FontAwesomeIcon icon={faFileAlt} />,
                  onClick: async () => {
                    navigate(`/teacher/exams/details/${exam.id}`);
                  },
                },
                {
                  tooltip: t('printExamTooltip'),
                  icon: <FontAwesomeIcon icon={faPrint} />,
                  onClick: () => handlePrintExam(exam),
                  disabled: printLoading === exam.id,
                },
                {
                  tooltip: t('startExamTooltip'),
                  icon: <FontAwesomeIcon icon={faPlay} />,
                  onClick: () => handleStartExam(exam),
                  disabled: startExamLoading === exam.id,
                },
                {
                  tooltip: t('reset'),
                  icon: <FontAwesomeIcon icon={faRedo} />,
                  onClick: () => handleResetExam(exam),
                  disabled: examToReset === exam.id,
                },
                {
                  tooltip: t('assignStudentTooltip'),
                  icon: <FontAwesomeIcon icon={faPaperPlane} />,
                  onClick: async () => {
                    handleAssignUser(exam);
                  },
                  disabled:
                    assignLoading && assignExam && assignExam.id === exam.id,
                },
                {
                  tooltip: t('delete'),
                  icon: <FontAwesomeIcon icon={faTrash} />,
                  onClick: () => handleDelete(exam),
                  className: "delete",
                },
              ].forEach(addAction);
            }

            // Sort actions by tooltip
            actions.sort((a, b) =>
              (a.tooltip || "").localeCompare(b.tooltip || "")
            );
            return actions;
          },
        ]}
        isLoading={loading}
        isPaginated={true}
        pageNumber={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={(newSize) => {
          setPageSize(newPageSize => {
            if (newPageSize !== newSize) {
              setCurrentPage(1);
              return newSize;
            }
            return newPageSize;
          });
        }}
        searchPlaceholder={t('searchExamPlaceholder')}
        columnResizable={true}
        onSearch={handleSearch}
        isColumnDraggable={true}
        isColumnSelectable={true}
        emptyStateMessage={t('noExamsAvailable')}
        useGroupBy={true}
        multiSelect={true}
        multiSelectAPI="Exam/delete"
        onMultiSelectDeleteSuccess={() => fetchExams(currentPage, searchQuery)}
      />



      <style jsx>{`
        .exam-manager {
          padding: 24px;
          background: #fff;
          min-height: calc(100vh - 48px);
          width: 100%;
          overflow-x: hidden;
          border-radius: 30px;
        }


        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 24px;
          flex: 1;
          min-width: 300px;
        }

        .header-left h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1a1f36;
          margin: 0;
            font-family: "Zain", sans-serif;
        }
        .header-right {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 12px 16px;
          border: 2px solid #eef2f7;
          border-radius: 12px;
          background: #f8fafc;
          color: #1a1f36;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 160px;
            font-family: "Zain", sans-serif;
        }

        .filter-select:hover {
          border-color: #4a90e2;
          background: #fff;
        }

        .filter-select:focus {
          outline: none;
          border-color: #4a90e2;
          box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.1);
        }

        .add-exam-btn {
          padding: 12px 24px;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-size: 14px;
          font-weight: 500;
            font-family: "Zain", sans-serif;
        }

        .add-exam-btn:hover {
          background: #4c51bf;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        .add-exam-btn svg {
          width: 16px;
          height: 16px;
        }


        .ex_status-badge-btn {
          font: inherit;
          padding: 0.5em 1.2em;
          border-radius: 8px;
          transition: box-shadow 0.2s;
        }
        .ex_status-badge-btn:hover {
          box-shadow: 0 2px 8px rgba(74, 144, 226, 0.13);
          filter: brightness(1.08);
        }
      `}</style>
      <style>{`
.circle-loader {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 3px solid #fff;
  border-top: 3px solid #3085d6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  0% { transform: rotate(0deg);}
  100% { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
};

export default ExamManager;
